/**
 * Mini: tiny, decorative copies of the app's real controls, to draw a guide step (HelpStep's `illustration`).
 * They use the app's own look (FolderBar, FilterChips, the view tabs, the bank's buttons, the dialogs) at a small
 * size (11px on phones, 12px from md up); they are spans and divs, never focusable, and HelpStep hides its
 * illustration from screen readers (aria-hidden), so the step's text must say everything the picture shows.
 * Build pictures from these parts only (no Tailwind of your own): rows with MiniRow, boxes with MiniCard.
 *
 *   import { MiniButton, MiniCard, MiniChip, MiniRow, MiniScreen, MiniText } from "../Mini"; // from guides/
 *
 *   <MiniScreen title={t("nav.question_bank")}>
 *     <MiniRow>
 *       <MiniText variant="caps">{t("folders.label")}</MiniText>
 *       <MiniChip variant="folder" selected count={139} mark="1">{t("help.common.example.exam")}</MiniChip>
 *       <MiniButton variant="quiet" chevron>{t("folders.more_folders", { count: 3 })}</MiniButton>
 *     </MiniRow>
 *     <MiniCard tone="blue">
 *       <MiniRow className="justify-between">
 *         <MiniText variant="strong">{t("folders.move.selected", { count: 2 })}</MiniText>
 *         <MiniButton variant="primary" icon={FolderArrowDownIcon} mark>{t("folders.move.button")}</MiniButton>
 *       </MiniRow>
 *     </MiniCard>
 *   </MiniScreen>
 *
 * Exports (every text is a translated string; the labels of real buttons come from their existing i18n keys):
 * Frames and layout
 * - MiniScreen({ title, children }): a small window frame; its body stacks its children (space-y).
 * - MiniDialog({ title, children, footer }): a dialog over a dimmed page (CustomDialog's look): a title, the body
 *   (stacked) and `footer`, a right-aligned row of MiniButtons (e.g. Cancel, then the marked primary one).
 * - MiniCard({ tone, icon, aside, mark, className, children }): a box. tone "white" (default: a card, e.g. a list
 *   item), "slate" (a quiet box), "blue" (the selection bar, a notice), "amber" (a warning), "red" (an error),
 *   "green" (done); `icon` a leading heroicon in the tone's colour (e.g. ExclamationTriangleIcon) or a leading node
 *   (e.g. <MiniCheck checked />); `aside` a node at the right end (a badge, a MiniIcon, a button); plain text
 *   inside takes the tone's colour; children stack (rows with MiniRow). `mark` marks the whole card.
 * - MiniRow({ children, scroll, className }): a wrapping row (flex-wrap, items-center, gap-1.5): use it for every
 *   row, so nothing overflows at 390px; className for "justify-between" / "justify-end" / "flex-nowrap" /
 *   "gap-y-3". `scroll`: one line running on past the right edge, faded (a row that scrolls sideways on phones,
 *   like the status chips); do not mark a control inside it.
 * - MiniStack({ tight, children }): parts under each other without a frame (e.g. a chips row above a MiniCard);
 *   `tight` for the lines of one block (a caption above a value).
 * - MiniDivider(): a thin line between stacked parts (e.g. the folder row and the subject row).
 * Texts and icons
 * - MiniText({ variant: "body" | "muted" | "strong" | "title" | "caps", truncate, children, className }): small
 *   text; `truncate` makes it one line with "…" (e.g. a question's name in a table cell).
 * - MiniIcon({ icon, tone: "slate" | "dark" | "blue" | "amber" | "red" | "green" }): a bare icon in a row.
 * Controls
 * - MiniButton({ variant: "primary" | "quiet" | "outline" | "danger" | "link", icon, chevron, children, mark }):
 *   a button; "link" is the blue text action ("+ Add subject": icon={PlusIcon}); `chevron` adds ▾ (a dropdown,
 *   "3 more folders ▾", "More filters ▾"); with no children a square icon button (icon={EllipsisHorizontalIcon}
 *   for "⋯").
 * - MiniChip({ variant: "filter" | "folder" | "subject", selected, dot, count, icon, children, mark }): a pill.
 *   "filter" (default) is FilterChips (selected = dark; `dot` = a CHIP_TONES name: slate, amber, blue, violet,
 *   cyan, green, red; count 0 = muted), "folder" the folder row (selected = light blue), "subject" the subject
 *   chips (selected = blue); `icon` a leading icon (e.g. the role's icon on a subject chip).
 * - MiniDecision({ code, selected, children, mark }): a step of the review's decision panel (DecisionPanel): a
 *   button in the colour of the status `code` it leads to ("UNDER_REVIEW", "APPROVED", "CHANGES_REQUESTED",
 *   "DECLINED", "ARCHIVED"), with its icon; `selected` fills it, as when chosen.
 * - MiniTabs({ items, current, mark, fill }): the segmented switch; items are strings or { label, icon };
 *   `current` and `mark` are item indexes; `fill` shares the row out equally on phones, a long label taking two
 *   lines (the subject's view tabs do that), from md up the tabs keep their own width.
 * - MiniField({ label, value, placeholder, select, icon, mark, className }): an input (or a select with `select`)
 *   with an optional label above; `icon` is a leading icon (e.g. MagnifyingGlassIcon for a search); className
 *   e.g. "flex-1" to fill a row.
 * - MiniCheck({ checked, children, mark }): a checkbox with an optional label.
 * - MiniRadio({ checked, children, hint, variant: "card" | "plain", mark }): a radio option; "card" (default) is
 *   the bordered option card (blue when chosen) with a `hint` line under the label, "plain" a bare radio + label.
 * - MiniSwitch({ on, children, mark }): an on/off switch with an optional label.
 * - MiniStepper({ value, label, mark }): a − value + counter with an optional label.
 * Data
 * - MiniTable({ columns, rows, markRow, markCell }): a table; columns are strings or { label, width, align }
 *   (width any CSS width, e.g. "2.75rem" or "40%"; align "center" | "right"; a label may be a node, e.g. a
 *   MiniLevel; a long header wraps, a string cell is one line with "…"); rows are arrays of cells (strings or Mini
 *   nodes). `markRow` (index) tints that row and marks its cell `markCell` (default 0) with a pointer.
 * - MiniStatus({ code, mark }): a question's status pill, as the lists show it: its colour and its translated
 *   label from the status code ("APPROVED", "UNDER_REVIEW", "PENDING_REVIEW", "CHANGES_REQUESTED",
 *   "RESUBMITTED", "DECLINED", "DRAFT", "ARCHIVED"). Prefer short ones in tables (APPROVED, UNDER_REVIEW, DECLINED).
 * - MiniBadge({ tone, icon, mark, children, className }): any other pill, in CHIP_TONES[tone].badge colours
 *   (slate (default), amber, blue, violet, cyan, green, red) or "indigo" (a passage's tag, with
 *   icon={BookOpenIcon}); pass `className` instead of `tone` for any other colours.
 * - MiniLevel({ level: "A" | "B" | "C", points }): the difficulty pill, "A · 3" (points = false: "A").
 * - MiniProgress({ value, max, label }): a progress bar with "value/max" (green when full).
 * - MiniMenu({ items: [{ label, icon, danger, hint, indent, selected, mark }], mark, title, full }): an open menu
 *   or list card ("⋯", a dropdown, the phones' folder sheet): `hint` a muted text on the right (a count, a role),
 *   `indent` nests the item under the one before, `selected` the chosen one (blue), an item's `mark` (true or a
 *   label like "2") marks it; `title` a small caption on top; `full` takes the whole width; the menu's `mark` is an
 *   item index (the same as that item's mark: true).
 * The marker
 * - Mark({ children, pointer, label, round, block }): the "click here" marker around anything: a blue ring and a
 *   small pulsing dot (motion-safe), with `pointer` a cursor, with `label` a tiny bubble instead of the dot (e.g.
 *   "1", "2" for an order). `round`: "full" | "lg" (default) | "md" to follow the child's corners; `block`: full
 *   width (around a whole row) instead of inline.
 * The `mark` prop of the controls is a shorthand for Mark: true = ring, dot and cursor; a string = the same with
 * that label (e.g. mark="1"); an object = Mark's own props, e.g. mark={{ pointer: false }}. One marker per picture,
 * or numbered ones. The cursor hangs ~12px below and right of the marked control: keep it off the picture's very
 * bottom edge, and give a wrapping row room under it (MiniRow className="gap-y-3").
 * Width: an illustration gets ~300px on a phone and ~470px on a wide screen; rows wrap (MiniRow), texts truncate.
 */
import React from "react";
import { CheckIcon, ChevronDownIcon, MinusIcon, PlusIcon } from "@heroicons/react/20/solid";
import { CursorArrowRaysIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { CHIP_TONES } from "../QuestionList/FilterChips";
import { statusText, statusTone } from "../QuestionList/questionStatus";
import { DIFFICULTY_POINTS } from "../Difficulty/Difficulty";
import { stepLook } from "../../pages/QuestionPage/review/stepLooks";

// Sizes: compact on phones, a step bigger from md up, where the panel is 36rem wide. Nothing under 10px.
const TEXT_SIZE = "text-[11px] md:text-xs";
const SMALL_SIZE = "text-[11px]";
const CAPS_SIZE = "text-[10px]";
const HEIGHT = "h-6 md:h-7";
const SQUARE = "h-6 w-6 md:h-7 md:w-7";
const ICON = "h-3.5 w-3.5 shrink-0";
const SMALL_ICON = "h-3 w-3 shrink-0 md:h-3.5 md:w-3.5";

const ROUND = { full: "rounded-full", lg: "rounded-lg", md: "rounded-md", none: "" };

export function Mark({ children, pointer = false, label, round = "lg", block = false, className = "" }) {
  return (
    <span
      className={`relative ${block ? "flex w-full" : "inline-flex"} min-w-0 max-w-full align-middle ring-2 ring-blue-500 ring-offset-2 ring-offset-white ${
        ROUND[round] ?? ROUND.lg
      } ${className}`}
    >
      {children}
      {label ? (
        <span className="pointer-events-none absolute -right-2 -top-2.5 z-[1] inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-white">
          {label}
        </span>
      ) : (
        <span className="pointer-events-none absolute -right-1 -top-1 z-[1] flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 motion-safe:animate-ping" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-blue-600 ring-2 ring-white" />
        </span>
      )}
      {pointer && (
        <CursorArrowRaysIcon className="pointer-events-none absolute -bottom-3 -right-2.5 z-[1] h-5 w-5 text-slate-800 drop-shadow-[0_1px_1px_rgba(255,255,255,0.9)]" />
      )}
    </span>
  );
}

// The `mark` shorthand of the controls: true, a label string or Mark's props; `block` for a full-width control.
function withMark(mark, node, round, block = false) {
  if (!mark) return node;
  const props = mark === true ? { pointer: true } : typeof mark === "object" ? mark : { pointer: true, label: mark };
  return (
    <Mark round={round} block={block} {...props}>
      {node}
    </Mark>
  );
}

// ---- Frames and layout ----

export function MiniScreen({ title, children }) {
  return (
    <div className="w-full min-w-0 rounded-lg bg-white shadow-sm ring-1 ring-slate-200">
      <div className="flex min-w-0 items-center gap-2 rounded-t-lg border-b border-slate-100 bg-slate-50 px-2.5 py-1.5">
        <span className="flex shrink-0 gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
          <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
          <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
        </span>
        {title && <span className={`min-w-0 truncate font-medium text-slate-500 ${SMALL_SIZE}`}>{title}</span>}
      </div>
      <div className="min-w-0 space-y-2.5 p-3">{children}</div>
    </div>
  );
}

export function MiniDialog({ title, children, footer }) {
  return (
    <div className="w-full min-w-0 rounded-lg bg-slate-900/10 p-2.5 md:p-4">
      <div className="mx-auto w-full min-w-0 max-w-[22rem] space-y-2.5 rounded-xl bg-white p-3 shadow-lg ring-1 ring-slate-900/5">
        {title && <div className="truncate text-[13px] font-semibold text-slate-900 md:text-sm">{title}</div>}
        {children && <div className="min-w-0 space-y-2">{children}</div>}
        {footer && <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5 gap-y-3 pt-1">{footer}</div>}
      </div>
    </div>
  );
}

const CARD = {
  white: "bg-white text-slate-700 shadow-sm ring-1 ring-slate-200",
  slate: "bg-slate-50 text-slate-700 ring-1 ring-inset ring-slate-200",
  blue: "bg-blue-50 text-blue-900 ring-1 ring-inset ring-blue-200",
  amber: "bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-600/20",
  red: "bg-red-50 text-red-800 ring-1 ring-inset ring-red-600/15",
  green: "bg-emerald-50 text-emerald-900 ring-1 ring-inset ring-emerald-600/20",
};
const ICON_TONES = {
  white: "text-slate-400",
  slate: "text-slate-400",
  dark: "text-slate-700",
  blue: "text-blue-600",
  amber: "text-amber-600",
  red: "text-red-600",
  green: "text-emerald-600",
};

export function MiniCard({ tone = "white", icon: Icon, aside, mark, className = "", children }) {
  const card = (
    <div className={`flex w-full min-w-0 items-start gap-2 rounded-lg p-2 md:p-2.5 ${TEXT_SIZE} ${CARD[tone] || CARD.white} ${className}`}>
      {/* A heroicon in the tone's colour, or a node (e.g. a MiniCheck). */}
      {React.isValidElement(Icon) ? (
        <span className="mt-px flex shrink-0">{Icon}</span>
      ) : (
        Icon && <Icon className={`mt-px ${SMALL_ICON} ${ICON_TONES[tone] || ICON_TONES.white}`} />
      )}
      <div className="min-w-0 flex-1 space-y-1.5">{children}</div>
      {aside && <span className="flex shrink-0 items-center self-center">{aside}</span>}
    </div>
  );
  return withMark(mark, card, "lg", true);
}

// `scroll`: one line that runs on past the right edge (a row that scrolls sideways on phones, e.g. the status
// chips), faded out there. A marked control inside it would lose its cursor: mark something else.
export function MiniRow({ children, scroll = false, className = "" }) {
  return (
    <div
      className={`flex min-w-0 items-center gap-1.5 ${
        scroll ? "flex-nowrap overflow-hidden [mask-image:linear-gradient(to_right,black_80%,transparent)] [&>*]:shrink-0" : "flex-wrap"
      } ${className}`}
    >
      {children}
    </div>
  );
}

// Parts under each other without a frame (e.g. a chips row above a filters card); `tight` for lines of one block.
export function MiniStack({ tight = false, children }) {
  return <div className={`min-w-0 ${tight ? "space-y-0.5" : "space-y-2.5"}`}>{children}</div>;
}

export function MiniDivider() {
  return <div className="h-px w-full bg-slate-200/70" />;
}

// ---- Texts and icons ----

const TEXT = {
  body: `${TEXT_SIZE} text-slate-700`,
  muted: `${TEXT_SIZE} text-slate-500`,
  strong: `${TEXT_SIZE} font-semibold text-slate-900`,
  title: "text-[13px] md:text-sm font-semibold tracking-tight text-slate-900",
  caps: `${CAPS_SIZE} font-semibold uppercase tracking-wider text-slate-500`,
};

export function MiniText({ variant = "body", truncate = false, children, className = "" }) {
  return <span className={`min-w-0 ${truncate ? "block truncate" : ""} ${TEXT[variant] || TEXT.body} ${className}`}>{children}</span>;
}

export function MiniIcon({ icon: Icon, tone = "slate" }) {
  return Icon ? <Icon className={`${SMALL_ICON} ${ICON_TONES[tone] || ICON_TONES.slate}`} /> : null;
}

// ---- Controls ----

const BUTTON = {
  primary: "bg-blue-600 text-white shadow-sm shadow-blue-600/25",
  quiet: "text-slate-600",
  outline: "bg-white text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300",
  danger: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200",
  link: "text-blue-700",
};
const CHEVRON = { primary: "text-white/80", link: "text-blue-500" };

export function MiniButton({ variant = "outline", icon: Icon, chevron = false, children, mark }) {
  const iconOnly = !children;
  return withMark(
    mark,
    <span
      className={`inline-flex min-w-0 max-w-full items-center justify-center gap-1 whitespace-nowrap rounded-md font-medium ${TEXT_SIZE} ${
        iconOnly ? SQUARE : `${HEIGHT} ${variant === "link" ? "px-1" : "px-2"}`
      } ${BUTTON[variant] || BUTTON.outline}`}
    >
      {Icon && <Icon className={ICON} />}
      {children && <span className="truncate">{children}</span>}
      {chevron && <ChevronDownIcon className={`${ICON} ${CHEVRON[variant] || "text-slate-400"}`} />}
    </span>,
    "md"
  );
}

const CHIP = {
  filter: {
    selected: "bg-slate-900 font-semibold text-white ring-slate-900",
    idle: "bg-white font-medium text-slate-700 ring-slate-200",
    empty: "font-normal text-slate-500 ring-slate-200",
    count: ["text-white/70", "text-slate-500"],
    icon: ["text-white/70", "text-slate-400"],
  },
  folder: {
    selected: "bg-blue-50 font-semibold text-blue-700 ring-blue-200",
    idle: "bg-white font-medium text-slate-700 ring-slate-200",
    empty: "bg-white font-medium text-slate-700 ring-slate-200",
    count: ["text-blue-700", "text-slate-500"],
    icon: ["text-blue-600", "text-slate-400"],
  },
  subject: {
    selected: "bg-blue-600 font-medium text-white ring-blue-600",
    idle: "bg-white font-medium text-slate-700 ring-slate-200",
    empty: "bg-white font-medium text-slate-700 ring-slate-200",
    count: ["text-blue-100", "text-slate-400"],
    icon: ["text-blue-100", "text-slate-400"],
  },
};

export function MiniChip({ variant = "filter", selected = false, dot, count, icon: Icon, children, mark }) {
  const look = CHIP[variant] || CHIP.filter;
  const empty = count === 0;
  // As on the real rows: a folder or subject with no questions shows no count.
  const showCount = count !== undefined && count !== null && (variant === "filter" || count > 0);
  const tone = dot ? CHIP_TONES[dot] || CHIP_TONES.slate : null;
  return withMark(
    mark,
    <span
      className={`inline-flex min-w-0 max-w-full items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 ring-1 ring-inset ${HEIGHT} ${TEXT_SIZE} ${
        selected ? look.selected : empty ? look.empty : look.idle
      }`}
    >
      {tone && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${empty && !selected ? "bg-slate-300" : tone.dot}`} />}
      {Icon && <Icon className={`${SMALL_ICON} ${look.icon[selected ? 0 : 1]}`} />}
      <span className="truncate">{children}</span>
      {showCount && <span className={`font-semibold tabular-nums ${SMALL_SIZE} ${look.count[selected ? 0 : 1]}`}>{count}</span>}
    </span>,
    "full"
  );
}

export function MiniDecision({ code, selected = false, children, mark }) {
  const look = stepLook(code);
  return withMark(
    mark,
    <span
      className={`inline-flex min-w-0 max-w-full items-center gap-1 whitespace-nowrap rounded-md px-2 font-semibold shadow-sm ring-1 ring-inset ${HEIGHT} ${TEXT_SIZE} ${
        selected ? look.on : look.idle
      }`}
    >
      <look.Icon className={SMALL_ICON} />
      <span className="truncate">{children}</span>
    </span>,
    "md"
  );
}

export function MiniTabs({ items = [], current = 0, mark, fill = false }) {
  return (
    <span className={`${fill ? "flex w-full md:inline-flex md:w-auto" : "inline-flex"} min-w-0 max-w-full gap-0.5 rounded-lg bg-slate-100 p-0.5`}>
      {items.map((item, index) => {
        const { label, icon: Icon } = typeof item === "object" && item !== null ? item : { label: item };
        const on = index === current;
        const tab = (
          <span
            className={`inline-flex min-w-0 items-center gap-1 rounded-md px-1.5 font-medium md:px-2 ${TEXT_SIZE} ${
              fill
                ? "min-h-[1.5rem] w-full justify-center py-0.5 text-center leading-tight md:h-7 md:min-h-0 md:w-auto md:whitespace-nowrap md:py-0"
                : `${HEIGHT} whitespace-nowrap`
            } ${on ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600"}`}
          >
            {Icon && <Icon className={`${SMALL_ICON} ${on ? "text-blue-600" : "text-slate-400"}`} />}
            <span className={fill ? "min-w-0 md:truncate" : "truncate"}>{label}</span>
          </span>
        );
        const node = index === mark ? withMark(fill ? { pointer: true, block: true } : true, tab, "md") : tab;
        return (
          <span key={index} className={fill ? "flex min-w-0 flex-1 md:flex-none" : "inline-flex min-w-0"}>
            {node}
          </span>
        );
      })}
    </span>
  );
}

export function MiniField({ label, value, placeholder, select = false, icon: Icon, mark, className = "" }) {
  const box = (
    <span className={`flex w-full min-w-0 items-center gap-1 rounded-md bg-white px-2 shadow-sm ring-1 ring-inset ring-slate-300 ${HEIGHT} ${TEXT_SIZE}`}>
      {Icon && <Icon className={`${SMALL_ICON} text-slate-400`} />}
      <span className={`min-w-0 flex-1 truncate ${value ? "text-slate-900" : "text-slate-400"}`}>{value || placeholder}</span>
      {select && <ChevronDownIcon className={`${SMALL_ICON} text-slate-400`} />}
    </span>
  );
  return (
    <span className={`inline-flex min-w-[6rem] max-w-full flex-col gap-0.5 ${className}`}>
      {label && <span className={`truncate font-medium text-slate-500 ${SMALL_SIZE}`}>{label}</span>}
      {mark ? withMark(mark, box, "md", true) : box}
    </span>
  );
}

export function MiniCheck({ checked = false, children, mark }) {
  const box = (
    <span
      className={`inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] ring-1 ring-inset ${
        checked ? "bg-blue-600 ring-blue-600" : "bg-white ring-slate-300"
      }`}
    >
      {checked && <CheckIcon className="h-3 w-3 text-white" />}
    </span>
  );
  return withLabel(box, children, mark, "md");
}

// A small control and its label: the marker goes round both (or round the control alone when it has no label).
function withLabel(control, label, mark, round) {
  if (!label) return withMark(mark, control, round);
  return withMark(
    mark,
    <span className={`inline-flex min-w-0 items-center gap-1.5 text-slate-700 ${TEXT_SIZE} ${mark ? "px-1 py-0.5" : ""}`}>
      {control}
      <span className="truncate">{label}</span>
    </span>,
    "md"
  );
}

export function MiniRadio({ checked = false, children, hint, variant = "card", mark }) {
  const dot = (
    <span
      className={`mt-px inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full ring-1 ring-inset ${
        checked ? "bg-blue-600 ring-blue-600" : "bg-white ring-slate-300"
      }`}
    >
      {checked && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
    </span>
  );
  const text = (children || hint) && (
    <span className="min-w-0">
      {children && <span className={`block ${variant === "card" ? "font-medium text-slate-900" : "text-slate-700"}`}>{children}</span>}
      {hint && <span className={`block text-slate-500 ${SMALL_SIZE}`}>{hint}</span>}
    </span>
  );
  if (variant === "card") {
    return withMark(
      mark,
      <span
        className={`flex w-full min-w-0 items-start gap-2 rounded-lg px-2.5 py-2 ring-1 ring-inset ${TEXT_SIZE} ${
          checked ? "bg-blue-50 ring-blue-600" : "bg-white ring-slate-200"
        }`}
      >
        {dot}
        {text}
      </span>,
      "lg",
      true
    );
  }
  if (!text) return withMark(mark, dot, "full");
  return withMark(
    mark,
    <span className={`inline-flex min-w-0 items-start gap-1.5 ${TEXT_SIZE} ${mark ? "px-1 py-0.5" : ""}`}>
      {dot}
      {text}
    </span>,
    "md"
  );
}

export function MiniSwitch({ on = false, children, mark }) {
  const control = (
    <span className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full ${on ? "bg-blue-600" : "bg-slate-300"}`}>
      <span className={`inline-block h-3 w-3 rounded-full bg-white shadow ${on ? "translate-x-3.5" : "translate-x-0.5"}`} />
    </span>
  );
  return withLabel(control, children, mark, "full");
}

export function MiniStepper({ value, label, mark }) {
  const control = (
    <span className={`inline-flex items-center rounded-md bg-white shadow-sm ring-1 ring-inset ring-slate-300 ${HEIGHT}`}>
      <span className="inline-flex h-full w-5 items-center justify-center text-slate-500">
        <MinusIcon className="h-3 w-3" />
      </span>
      <span className={`min-w-[1.25rem] text-center font-semibold tabular-nums text-slate-900 ${TEXT_SIZE}`}>{value}</span>
      <span className="inline-flex h-full w-5 items-center justify-center text-slate-500">
        <PlusIcon className="h-3 w-3" />
      </span>
    </span>
  );
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {label && <span className={`truncate font-medium text-slate-500 ${SMALL_SIZE}`}>{label}</span>}
      {mark ? withMark(mark, control, "md") : control}
    </span>
  );
}

// ---- Data ----

const columnOf = (column) => (typeof column === "object" && column !== null && !React.isValidElement(column) ? column : { label: column });
const ALIGN_TEXT = { center: "text-center", right: "text-right" };
const ALIGN_FLEX = { center: "justify-center", right: "justify-end" };

export function MiniTable({ columns = [], rows = [], markRow, markCell = 0 }) {
  const specs = columns.map(columnOf);
  return (
    <div className="w-full min-w-0 rounded-lg bg-white shadow-sm ring-1 ring-slate-200">
      <table className={`w-full table-fixed border-separate border-spacing-0 text-left ${TEXT_SIZE}`}>
        <colgroup>
          {specs.map(({ width }, index) => (
            <col key={index} style={width ? { width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {specs.map(({ label, align }, index) => (
              <th
                key={index}
                className={`break-words border-b border-slate-200 px-2 py-1.5 align-bottom font-semibold uppercase leading-tight tracking-wide text-slate-500 ${CAPS_SIZE} ${ALIGN_TEXT[align] || ""}`}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, rowIndex) => {
            const marked = rowIndex === markRow;
            return (
              <tr key={rowIndex}>
                {cells.map((cell, cellIndex) => {
                  const content = typeof cell === "string" ? <span className="block truncate">{cell}</span> : cell;
                  return (
                    <td
                      key={cellIndex}
                      className={`px-2 py-1.5 align-middle text-slate-700 ${rowIndex > 0 ? "border-t border-slate-100" : ""} ${
                        marked ? "bg-blue-50/70" : ""
                      }`}
                    >
                      <span className={`flex min-w-0 ${ALIGN_FLEX[specs[cellIndex]?.align] || ""}`}>
                        {marked && cellIndex === markCell ? (
                          <Mark pointer round="md">
                            <span className="min-w-0 px-1">{content}</span>
                          </Mark>
                        ) : (
                          <span className="min-w-0 max-w-full">{content}</span>
                        )}
                      </span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Beyond the status tones: a passage's tag (PassageChip) is indigo.
const BADGE_TONES = { indigo: "bg-indigo-50 text-indigo-700 ring-indigo-600/20" };

export function MiniBadge({ tone, icon: Icon, mark, children, className = "" }) {
  const colours = BADGE_TONES[tone] || (tone || !className ? (CHIP_TONES[tone] || CHIP_TONES.slate).badge : "");
  return withMark(
    mark,
    <span
      className={`inline-flex min-w-0 max-w-full items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-px font-medium ring-1 ring-inset md:px-2 ${SMALL_SIZE} ${colours} ${className}`}
    >
      {Icon && <Icon className="h-3 w-3 shrink-0" />}
      <span className="truncate">{children}</span>
    </span>,
    "full"
  );
}

export function MiniStatus({ code, mark }) {
  const [t] = useTranslation();
  return (
    <MiniBadge className={statusTone(code).badge} mark={mark}>
      {statusText(t, code)}
    </MiniBadge>
  );
}

// The difficulty pills' colours (Difficulty.js): A sky, B indigo, C violet.
const LEVELS = {
  A: "bg-sky-50 text-sky-700 ring-sky-600/20",
  B: "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  C: "bg-violet-50 text-violet-700 ring-violet-600/20",
};

export function MiniLevel({ level, points = true }) {
  if (!LEVELS[level]) return null;
  return (
    <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-1.5 py-px font-semibold ring-1 ring-inset md:px-2 ${SMALL_SIZE} ${LEVELS[level]}`}>
      {level}
      {points && <span className="font-normal opacity-75">&nbsp;·&nbsp;{DIFFICULTY_POINTS[level]}</span>}
    </span>
  );
}

export function MiniProgress({ value = 0, max = 0, label }) {
  const share = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const full = max > 0 && value >= max;
  return (
    <span className="block w-full min-w-0">
      <span className={`mb-1 flex min-w-0 items-center justify-between gap-2 ${SMALL_SIZE}`}>
        <span className="min-w-0 truncate text-slate-500">{label}</span>
        <span className="shrink-0 font-semibold tabular-nums text-slate-700">
          {value}/{max}
        </span>
      </span>
      <span className="block h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
        <span className={`block h-full rounded-full ${full ? "bg-emerald-500" : "bg-blue-600"}`} style={{ width: `${share}%` }} />
      </span>
    </span>
  );
}

export function MiniMenu({ items = [], mark, title, full = false }) {
  return (
    <span className={`block ${full ? "w-full" : "w-48 max-w-full md:w-56"} rounded-lg bg-white p-1 shadow-lg ring-1 ring-slate-900/5`}>
      {title && <span className={`block truncate px-2 pb-0.5 pt-1 font-medium text-slate-500 ${SMALL_SIZE}`}>{title}</span>}
      {items.filter(Boolean).map(({ label, icon: Icon, danger = false, hint, indent = false, selected = false, mark: itemMark }, index) => {
        // The menu's `mark` (an index) or the item's own (true, or a label such as "2").
        const marked = index === mark || !!itemMark;
        const row = (
          <span
            className={`flex w-full min-w-0 items-center gap-1.5 rounded-md py-1 ${indent ? "pl-6 pr-2" : "px-2"} ${TEXT_SIZE} ${
              danger
                ? `text-red-600 ${marked ? "bg-red-50" : ""}`
                : selected
                  ? "bg-blue-50 font-semibold text-blue-700"
                  : marked
                    ? "bg-slate-100 text-slate-900"
                    : "text-slate-700"
            }`}
          >
            {Icon && <Icon className={`${ICON} ${selected ? "text-blue-600" : ""}`} />}
            <span className="min-w-0 flex-1 truncate">{label}</span>
            {hint !== undefined && hint !== null && hint !== "" && (
              <span className={`shrink-0 font-semibold tabular-nums ${SMALL_SIZE} ${selected ? "text-blue-700" : "text-slate-500"}`}>{hint}</span>
            )}
            {selected && <CheckIcon className={`${ICON} text-blue-600`} />}
          </span>
        );
        return marked ? (
          <Mark key={index} pointer round="md" block label={typeof itemMark === "string" ? itemMark : undefined}>
            {row}
          </Mark>
        ) : (
          <React.Fragment key={index}>{row}</React.Fragment>
        );
      })}
    </span>
  );
}
